import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { DATE_FORMATS } from "../constants";
import { utcToDatePicker } from "./converters/dates-helper";
import { truncateText } from "./string-helpers";

const sortRecent = (a, b) => {
  return b.utcDate - a.utcDate;
};

const sortOldest = (a, b) => {
  return a.utcDate - b.utcDate;
};

export const sortStatsByDate = (stats = [], orderingType = "recent") => {
  if (orderingType === "recent") return stats.sort(sortRecent);
  return stats.sort(sortOldest);
};

export const filterUnsetValues = (stats = [], field) => {
  return stats.filter((stat) => {
    return !!stat[field];
  });
};

// Some integrations (MyFitnessPal, Cronometer) fold the serving size into
// servingName itself (e.g. servingName: "60 gram" alongside servingSize: 60),
// so naively rendering `${servingSize} ${servingName}` doubles the number.
export const getServingLabel = (food) => {
  const name = (food.servingName ?? "").trim();
  const size = food.servingSize;
  if (size == null || size === "") return name;
  if (name?.startsWith(String(size))) return name;
  return `${size} ${name}`.trim();
};

export const filterStatsbyDateRange = ({ stats = [], dateTo, dateFrom }) => {
  dateTo = dateTo
    ? moment(dateTo, DATE_FORMATS.DATE_PICKER_FORMAT)
    : moment(new Date());
  dateFrom = moment(dateFrom, DATE_FORMATS.DATE_PICKER_FORMAT);

  const filteredStats = stats.filter((stat) => {
    const utcDate = moment(stat.utcDate.toString(), DATE_FORMATS.UTC);
    return utcDate.isSameOrAfter(dateFrom) && utcDate.isSameOrBefore(dateTo);
  });

  return filteredStats;
};

const getLineArrays = (length = 0, values = []) => {
  const arrays = values
    .map((val) => {
      const arr = [];
      for (let i = 0; i < length; i++) {
        arr.push(val);
      }
      // arr example value: [6, 6, ...]
      return arr;
    })
    .reverse();
  return arrays;
};

const projectDateAndValues = ({ stats = [], field, offset = 0 }) => {
  return stats
    .map((stat) => {
      return {
        meta: utcToDatePicker(stat.utcDate),
        value: stat[field] + offset,
      };
    })
    .reverse();
};

/**
 *
 * @param {stats} param0 objects list to map through
 * @param {fieldToConvert} param1, field name that has an utcDate (YYYYMMDD) format value ex: 20210925
 * @param {newField} param2, fieldName that will contain the convertedDate value to format MMM D, YYYY
 * @returns newObject with newField having the converted value
 */
export const mapUtcToDatePicker = ({ stats, fieldToConvert, newField }) => {
  const newStats = cloneDeep(stats);
  return newStats.map((stat) => {
    stat[newField] = utcToDatePicker(stat[fieldToConvert]);
    // example use case: stat["date"] = utcToDatePicker(stat["utcDate"]);
    return stat;
  });
};

export const filterAndSort = ({
  stats,
  dateTo,
  dateFrom,
  field,
  orderingType = "recent",
}) => {
  let result = filterStatsbyDateRange({ stats, dateTo, dateFrom });
  result = filterUnsetValues(result, field);
  result = sortStatsByDate(result, orderingType);
  return result;
};

/**
 *
 * @param {*} stats
 * @returns and array of dates from the stats array
 */
export const getLabels = (stats) => {
  const labels = stats.map((stat) => {
    return moment(stat.utcDate, DATE_FORMATS.UTC).format(
      DATE_FORMATS.DATE_PICKER_FORMAT
    );
  });
  return labels;
};

/**
 *
 * @param {Obj} stat: stats list
 * field: field to project
 * Projects the field in stats list
 * Adds additional fixed lines if needed
 * @returns
 */
export const readyChartData = ({
  stats,
  field,
  offset = 0,
  extraLinesValues = [],
}) => {
  const rpeLine = projectDateAndValues({ stats, field, offset });
  const referenceLines = getLineArrays(stats.length, extraLinesValues);
  referenceLines.push(rpeLine);
  return referenceLines;
};

export const getActiveDayStats = ({ activeDayUtc, stats }) => {
  return stats.find((stat) => stat.utcDate === activeDayUtc);
};

/**
 * Function to merge the foodLog macros first or cronometer data with internalFoodLog data
 * @param {*} internalFoodLog
 * @param {*} source
 * @returns
 */
export const mergeFoodLogs = (
  foodLog = [],
  internalFoodLog = [],
  source = "macrosFirst"
) => {
  const sources = source.split(",").map((s) => s.trim());

  if (
    sources.includes("macrosFirst") ||
    sources[0] === "mealPlanner" ||
    sources.includes("myfitnesspal")
  ) {
    return mergeMacrosFirst(foodLog, internalFoodLog);
  }

  if (sources.includes("cronometer")) {
    return mergeCronometer(foodLog, internalFoodLog);
  }

  return foodLog; // fallback
};

const excludedUnits = ["fl oz", "tbsp", "tsp", "ml", "oz", "g", "lb"];

/**
 * Function to merge the foodLog macros first data with internalFoodLog data
 * @param {*} foodLog
 * @param {*} internalFoodLog
 * @returns
 */
const mergeMacrosFirst = (foodLog, internalFoodLog) => {
  const mergedMeals = (foodLog?.meals || []).map((meal) => ({
    ...meal,
    foods: [...meal.foods],
  }));

  const mealNameMap = new Map(
    mergedMeals.map((meal) => [meal.name.toLowerCase(), meal])
  );

  for (const internalMeal of internalFoodLog?.meals || []) {
    const nameKey = internalMeal.name.toLowerCase();
    const existingMeal = mealNameMap.get(nameKey);

    const formattedFoods = internalMeal.foods.map((food) => {
      const name = food.servingName?.trim() || "";
      const lowerName = name.toLowerCase();

      const shouldPluralize =
        food.servingSize > 1 &&
        !lowerName.endsWith("s") &&
        name.length > 0 &&
        !excludedUnits.includes(lowerName);

      return {
        ...food,
        servingName: truncateText(shouldPluralize ? name + "s" : name, 15),
      };
    });

    if (existingMeal) {
      existingMeal.foods.push(...formattedFoods);

      existingMeal.proteins += internalMeal.proteins;
      existingMeal.carbs += internalMeal.carbs;
      existingMeal.fats += internalMeal.fats;
      existingMeal.fibers += internalMeal.fibers;
      existingMeal.kcal += internalMeal.kcal;
    } else {
      mergedMeals.push({
        ...internalMeal,
        foods: formattedFoods,
      });
    }
  }

  return {
    ...foodLog,
    meals: mergedMeals,
  };
};

/**
 * Function to merge the foodLog cronometer data with internalFoodLog data
 * @param {*} foodLog
 * @param {*} internalFoodLog
 * @returns
 */
const mergeCronometer = (foodLog, internalFoodLog) => {
  const mergedMeals = (foodLog?.meals || []).map((meal) => ({
    ...meal,
    foods: [...meal.foods],
  }));

  const mealNameMap = new Map(
    mergedMeals.map((meal) => [meal.name.toLowerCase(), meal])
  );

  for (const internalMeal of internalFoodLog?.meals || []) {
    const nameKey = internalMeal.name.toLowerCase();
    const existingMeal = mealNameMap.get(nameKey);

    const formattedFoods = internalMeal.foods.map((food) => {
      const servingName = food.servingName?.trim().toLowerCase() || "";

      const shouldPluralize =
        food.servingSize > 1 &&
        typeof food.servingName === "string" &&
        !servingName.endsWith("s") &&
        !excludedUnits.includes(servingName);

      const pluralizedServingName = shouldPluralize
        ? `${food.servingName}s`
        : food.servingName;

      return {
        ...food,
        servingName: truncateText(
          `${food.servingSize} - ${pluralizedServingName}`,
          15
        ),
      };
    });

    if (existingMeal) {
      existingMeal.foods.push(...formattedFoods);
      existingMeal.proteins += internalMeal.proteins;
      existingMeal.carbs += internalMeal.carbs;
      existingMeal.fats += internalMeal.fats;
      existingMeal.fibers += internalMeal.fibers;
      existingMeal.kcal += internalMeal.kcal;
    } else {
      mergedMeals.push({
        ...internalMeal,
        foods: formattedFoods,
      });
    }
  }
  return {
    ...foodLog,
    meals: mergedMeals,
  };
};

export const buildProgressPhotoPreviewHref = (
  type,
  pictureImg,
  threadId,
  dailyStatId,
  utcDate
) => {
  return `/actions/refresh?modal[type]=preview-photo&pictureImg=${pictureImg}&photoType=${type}&threadId=${threadId}&dailyStatId=${dailyStatId}&utcDate=${utcDate}`;
};
